<script setup lang="ts">
import type {EocSummaryChartData} from "@/api/endpoints/statisticsWeb.ts";
import {useI18n} from "vue-i18n";
import {computed, type ComputedRef, type PropType, ref} from "vue";
import {EpisodeOfCareSummaryChartMetricType} from "@/constants/enums.ts";
import BarGraph from "@/components/graphs/BarGraph.vue";
import FilledScatter from "@/components/graphs/FilledScatter.vue";

const props = defineProps({
  chart: {
    type: Object as PropType<EocSummaryChartData>,
    required: true
  }
});

const {t} = useI18n();

const graph = ref(),
  isVisible = ref(false);

const plotData = computed(() => {
  const x: string[] = [],
    y: (number | null)[] = [];
  
  props.chart?.dataPoints.forEach((p) => {
    x.push(p.label);
    y.push(p.value)
  });
  
  return [{x, y}];
});

const unitLbl: ComputedRef<string> = computed(() => {
  switch (props.chart?.metricType) {
    case EpisodeOfCareSummaryChartMetricType.Unknown:
      return '';
    case EpisodeOfCareSummaryChartMetricType.Steps:
      return 'k';
    default:
      return '°';
  }
});

defineExpose({
  async init() {
    graph.value.init();
    
    // await graph.value.update();

    isVisible.value = true;
  },
  reset() {
    graph.value.reset();
  }
});
</script>

<template>
  <div :class="['graph-container', {'invisible' : !isVisible}]">
    
    <!-- Title -->
    <h5>{{ props.chart?.metricLocalized }}</h5>
    
    <!-- Bar -->
    <BarGraph v-if="props.chart?.chartType === $zb.enums.EpisodeOfCareSummaryChartType.Bar"
              :height="240"
              :plot-data="plotData"
              ref="graph"
              :unit-label="unitLbl" />

    <!-- Line -->
    <FilledScatter v-if="props.chart?.chartType === $zb.enums.EpisodeOfCareSummaryChartType.Line"
                   :height="240"
                   :plot-data="plotData"
                   ref="graph"
                   :unit-label="unitLbl" />
  </div>
</template>

<style scoped lang="scss">
.graph-container {
  width: 100%;
  height: 320px;
  background-color: $white;
  padding: 20px;
  border: 1px solid $cnt-def-page-divider;
  border-radius: $generic-border-radius;
  
  &:not(:last-child) {
    margin-block-end: 16px;
  }
  
  h5 {
    line-height: 22px;
    margin-block-end: 12px;
  }
}
</style>